import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { emitToast } from '../../context/toast';

const JABEL_SLUG = 'jabel-coaching-online';

export default function JabelVrPromoCodesPanel({ brandSlug, readOnly = false }) {
  const [rows, setRows] = useState([]);
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);

  const allowed = String(brandSlug || '').trim() === JABEL_SLUG;

  const load = useCallback(async () => {
    if (!allowed) return;
    try {
      const res = await api.get('/trainers/me/vr-promo-codes');
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      emitToast({
        type: 'error',
        title: 'Error',
        message: err.response?.data?.error || 'No se pudieron cargar los códigos promocionales',
      });
    }
  }, [allowed]);

  useEffect(() => {
    load();
  }, [load]);

  if (!allowed) return null;

  const create = async () => {
    setBusy(true);
    try {
      const res = await api.post('/trainers/me/vr-promo-codes', { code, label });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setCode('');
      setLabel('');
      emitToast({ type: 'success', title: 'Listo', message: 'Código promocional creado.' });
    } catch (err) {
      emitToast({
        type: 'error',
        title: 'Error',
        message: err.response?.data?.error || 'No se pudo crear el código',
      });
    } finally {
      setBusy(false);
    }
  };

  const setActive = async (id, active) => {
    setBusy(true);
    try {
      const res = await api.patch(`/trainers/me/vr-promo-codes/${encodeURIComponent(id)}`, { active });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      emitToast({
        type: 'error',
        title: 'Error',
        message: err.response?.data?.error || 'No se pudo actualizar el código',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="training-light-island surface-readable border border-stone-200 rounded-xl p-4 space-y-3 bg-white">
      <div>
        <h4 className="font-semibold text-stone-900 text-sm">Códigos promocionales Viento Recio</h4>
        <p className="text-xs text-stone-500 mt-1">
          Tus clientes pueden escribir uno al registrarse en tu landing Training. Es opcional y no cambia el acceso a Viento Recio; solo te permite distinguir quién llegó con promoción.
        </p>
      </div>
      {!readOnly ? (
        <div className="flex flex-wrap gap-2 items-end">
          <label className="block min-w-[8rem] flex-1">
            <span className="text-xs font-semibold text-stone-700">Código</span>
            <input
              className="input mt-1 w-full font-mono text-sm uppercase"
              value={code}
              disabled={busy}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="EJ. VR2026"
            />
          </label>
          <label className="block min-w-[8rem] flex-1">
            <span className="text-xs font-semibold text-stone-700">Nombre interno</span>
            <input
              className="input mt-1 w-full text-sm"
              value={label}
              disabled={busy}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Campaña septiembre"
            />
          </label>
          <button type="button" className="btn btn-primary text-sm" disabled={busy} onClick={create}>
            Crear
          </button>
        </div>
      ) : null}
      <ul className="space-y-2">
        {rows.length === 0 ? (
          <li className="text-sm text-stone-500">Aún no hay códigos.</li>
        ) : rows.map((row) => (
          <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 text-sm border border-stone-100 rounded-lg px-3 py-2">
            <div>
              <span className="font-mono font-semibold text-stone-900">{row.code}</span>
              {row.label ? <span className="text-stone-600 ml-2">{row.label}</span> : null}
              <span className="text-stone-500 ml-2">{row.uses || 0} uso{(row.uses || 0) === 1 ? '' : 's'}</span>
              {row.active === false ? (
                <span className="ml-2 text-xs font-semibold text-stone-500">Inactivo</span>
              ) : (
                <span className="ml-2 text-xs font-semibold text-emerald-700">Activo</span>
              )}
            </div>
            {!readOnly ? (
              <button
                type="button"
                className="btn btn-secondary text-xs"
                disabled={busy}
                onClick={() => setActive(row.id, row.active === false)}
              >
                {row.active === false ? 'Activar' : 'Desactivar'}
              </button>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
